import React from "react";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Tooltip from "@material-ui/core/Tooltip";
import FacebookIcon from "@material-ui/icons/Facebook";
import InstagramIcon from "@material-ui/icons/Instagram";
import LanguageIcon from "@material-ui/icons/Language";
import SettingsInputAntennaIcon from "@material-ui/icons/SettingsInputAntenna";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";

import { i18n } from "../../translate/i18n";
import { getTokens } from "../../styles/tokens";
import formatSerializedId from "../../utils/formatSerializedId";
import { StatusBadge } from "../ui";

/* Peças de apresentação das telas de conexões (Canais, Gerenciar conexões e o modal por empresa):
 * ícone do canal, selo do estado da sessão e número formatado. */

// Cores de identidade dos canais (não são a marca do sistema).
export const CHANNEL_COLOR = { whatsapp: "#25D366", facebook: "#3b5998", instagram: "#e1306c", webchat: "#128C7E" };

export const channelIcon = (channel) => {
  switch (channel) {
    case "facebook":
      return <FacebookIcon style={{ color: CHANNEL_COLOR.facebook }} />;
    case "instagram":
      return <InstagramIcon style={{ color: CHANNEL_COLOR.instagram }} />;
    case "whatsapp":
    case "whatsapp_oficial":
      return <WhatsAppIcon style={{ color: CHANNEL_COLOR.whatsapp }} />;
    case "webchat":
      return <LanguageIcon style={{ color: CHANNEL_COLOR.webchat }} />;
    default:
      return <SettingsInputAntennaIcon color="disabled" />;
  }
};

export const channelLabel = (channel) =>
  channel === "whatsapp_oficial"
    ? "WhatsApp · API oficial"
    : channel === "whatsapp"
    ? "WhatsApp"
    : channel === "facebook"
    ? "Facebook"
    : channel === "instagram"
    ? "Instagram"
    : channel === "webchat"
    ? "Webchat"
    : channel;

export const STATUS_NAMES = {
  CONNECTED: "Conectado",
  DISCONNECTED: "Desconectado",
  OPENING: "Conectando",
  qrcode: "Aguardando QR Code",
  TIMEOUT: "Conexão expirada",
  PAIRING: "Aguardando pareamento",
};

// Explicação de cada estado (mesmas chaves de i18n de sempre), mostrada ao passar o mouse no selo.
const STATUS_TIPS = {
  DISCONNECTED: ["connections.toolTips.disconnected.title", "connections.toolTips.disconnected.content"],
  qrcode: ["connections.toolTips.qrcode.title", "connections.toolTips.qrcode.content"],
  CONNECTED: ["connections.toolTips.connected.title"],
  TIMEOUT: ["connections.toolTips.timeout.title", "connections.toolTips.timeout.content"],
  PAIRING: ["connections.toolTips.timeout.title", "connections.toolTips.timeout.content"],
};

/** Número da conexão (Baileys ou API oficial) com máscara; vazio quando não há. */
export const connectionNumber = (whatsApp) => {
  if (whatsApp?.channel === "webchat") return whatsApp?.webchatCode ? `/webchat/${whatsApp.webchatCode}` : "";
  const raw = whatsApp?.number || whatsApp?.phone_number;
  if (!raw) return "";
  // a máscara falha com números fora do padrão; nesse caso mostra como veio
  try {
    return formatSerializedId(String(raw)) || String(raw);
  } catch (e) {
    return String(raw);
  }
};

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    wrap: { display: "inline-flex", alignItems: "center", maxWidth: "100%" },
    tipText: { marginTop: 2, fontWeight: 400 },
    tile: {
      position: "relative",
      flex: "none",
      width: 40,
      height: 40,
      display: "grid",
      placeItems: "center",
      borderRadius: t.radius.sm,
      backgroundColor: t.muted,
      border: `1px solid ${t.line}`,
      "& .MuiSvgIcon-root": { fontSize: 22 },
    },
    tileSmall: { width: 34, height: 34, "& .MuiSvgIcon-root": { fontSize: 19 } },
    // cor cadastrada na conexão (vem de dado)
    dot: {
      position: "absolute",
      right: -4,
      bottom: -4,
      width: 12,
      height: 12,
      borderRadius: "50%",
      border: `2px solid ${t.paper}`,
    },
  };
});

/** Selo do estado da sessão (conectado, desconectado, aguardando QR, conectando…) com a explicação no tooltip. */
export function ConnectionStatus({ status, size }) {
  const classes = useStyles();
  const tip = STATUS_TIPS[status];
  const badge = (
    <StatusBadge
      status={status}
      size={size}
      label={STATUS_NAMES[status] || status || "Sem conexão"}
      icon={status === "OPENING" ? <CircularProgress size={10} thickness={6} color="inherit" /> : undefined}
    />
  );
  if (!tip) return badge;
  return (
    <Tooltip
      arrow
      title={
        <>
          <strong>{i18n.t(tip[0])}</strong>
          {tip[1] && <div className={classes.tipText}>{i18n.t(tip[1])}</div>}
        </>
      }
    >
      <span className={classes.wrap}>{badge}</span>
    </Tooltip>
  );
}

/** Quadradinho com o ícone do canal; `color` é a cor cadastrada na conexão. */
export function ChannelTile({ channel, color, small = false, className }) {
  const classes = useStyles();
  return (
    <div className={clsx(classes.tile, small && classes.tileSmall, className)}>
      {channelIcon(channel)}
      {color && <span className={classes.dot} style={{ backgroundColor: color }} />}
    </div>
  );
}
